import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LogService, LogEntry } from '../../core/services/log.service';

type Level = 'ALL' | 'LOG' | 'WARN' | 'ERROR';

@Component({
  selector: 'app-log-viewer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './log-viewer.html',
  styleUrls: ['./log-viewer.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogViewer implements OnInit, OnDestroy {
  visible = false;
  filter: Level = 'ALL';
  search = '';
  levels: Level[] = ['ALL', 'LOG', 'WARN', 'ERROR'];

  filteredLogs: LogEntry[] = [];

  private sub!: Subscription;

  constructor(private logService: LogService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    // S'abonne aux changements du service au lieu de polluer avec setInterval
    this.sub = this.logService.changes$.subscribe(() => {
      this.recompute();
      this.cdr.markForCheck();
    });
    this.recompute();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  open() {
    this.visible = true;
    this.recompute();
    this.cdr.markForCheck();
  }

  close() {
    this.visible = false;
    this.cdr.markForCheck();
  }

  setFilter(lvl: Level) {
    this.filter = lvl;
    this.recompute();
  }

  onSearchChange() {
    this.recompute();
  }

  // Recalcul explicite — plus de getter recalculé à chaque cycle
  private recompute() {
    this.filteredLogs = this.logService.getLogs()
      .slice()
      .reverse()
      .filter(e =>
        (this.filter === 'ALL' || e.level === this.filter) &&
        (!this.search || e.message.toLowerCase().includes(this.search.toLowerCase()))
      );
  }

  clear() {
    this.logService.clear();
    this.recompute();
    this.cdr.markForCheck();
  }

  export() {
    const text = this.logService.getLogs()
      .map(e => `[${e.time.toISOString()}] [${e.level}] ${e.message}`)
      .join('\n');

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: `logs-${new Date().toISOString().slice(0, 19)}.txt`
    });

    a.click();

    // Libère l'URL pour éviter la fuite mémoire
    URL.revokeObjectURL(url);
  }

  // trackBy correctement défini
  trackByIndex(index: number): number {
    return index;
  }

  formatTime(date: Date): string {
    // date est déjà un Date, pas besoin de new Date(date)
    return date.toTimeString().slice(0, 8);
  }
}